import * as actions from './action-types';
import { ActionDispatching } from 'app/types-shared';
import { errorSafe } from '../../infrastructure/redux';
import { api } from './api';
import { api as favouriteApi } from '../favourite/api';
import { ErrorNotification } from '../../infrastructure/notifications/user-messages';
import { SavedReportState, ViewMode } from './types-store';
import { createDescriptionSelector } from 'app/infrastructure/translate/index';
import { savedReportTranslation, form, ReportId, viewModeCacheKeySavedReports, viewModeCacheKeySavedCompanyReports, 
  viewModeCacheKeySavedDashboards } from 'app/area/saved-reports/constants';
import { SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import { mapCreateReport, mapUpdateFolder } from 'app/area/saved-reports/actions.mapper';
import { NewFolderData } from 'app/area/saved-reports/types';
import * as routerActions from 'app/area/shared/navigation';
import { routeConstants } from '../../routes/route-constants';
import { ValueChange } from 'app/infrastructure/simple-form/action-types';
import { ensureEmptyFoldersHaveChildrenProp } from 'app/area/saved-reports/create-saved-reports';
import { STATE } from 'app/constants';
import { replaceTemplateString, insertOptionalWordbreaks } from 'app/infrastructure/platform';
import { FormWireData } from 'app/infrastructure/simple-form/types';
import { Value } from 'app/types-shared';
import { clientCache } from 'app/infrastructure/universal';
import { ChangeViewMode, ChangeViewModeCompanyReport, ChangeViewModeDashboard } from './action-types';
import { ReportCategory } from 'app/area/case-report/case-listing/types';
import * as navigate from 'app/area/shared/navigation';

export const actionCreators = {
  saveReport: (serializedFormData: FormWireData, favouriteId: number,
    updateExisting:boolean, redirectId?: number, 
    selectedReportId?: number, originalFavouritId?: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestExecuteReport());

      const savedReportState = getState().savedReport as SavedReportState;
      const translate = createDescriptionSelector(savedReportState.texts);

      try {
        const result = await api.saveReport(mapCreateReport(favouriteId, serializedFormData, updateExisting));
        const textSuccessReportUpdated = selectedReportId === ReportId.Dashboard ? savedReportTranslation.success_dashboard_updated : 
          savedReportTranslation.success_report_updated;
        const textSuccessMessageReport = selectedReportId === ReportId.Dashboard ? savedReportTranslation.succes_message_dashboard : 
          savedReportTranslation.succes_message_report;
          const selectedFolderId = savedReportState?.folderDefinition?.favourite?.v;

        if (result && result.status === 200) {
          dispatch(new SuccessNotification(updateExisting ?
            translate(textSuccessReportUpdated) :
            translate(textSuccessMessageReport)
          ));
          dispatch(actionCreators.clearAll());
          if(selectedReportId === ReportId.CompanyReport) {
            routerActions.replace(routeConstants.selectSavedCompanyReportFor(selectedFolderId, originalFavouritId));
          }
          else if(selectedReportId === ReportId.Dashboard) {
            routerActions.replace(routeConstants.selectSavedDashboardFor(selectedFolderId, originalFavouritId));
          }
          else {
            routerActions.replace(routeConstants.selectSavedReportFor(selectedFolderId, redirectId || favouriteId));
          }
        }

      } catch (err) {
        dispatch(new actions.RequestFailed());
        dispatch(new ErrorNotification(err.userMessages || err.message));
      }
    },
  saveFolderFavourite: (serializedFormData: FormWireData, favouriteId: number, 
    reportCategories: number[], folderType: number): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestReports());
      const savedReportState = getState().savedReport as SavedReportState;
      const translate = createDescriptionSelector(savedReportState.texts);
      const data = {...mapUpdateFolder(serializedFormData), ...{folderType}};

      try {
        await api.updateFolder(data, favouriteId);

        dispatch(new SuccessNotification(
          translate(savedReportTranslation.folder_updated)
        ));
      } catch (err) {
        dispatch(new actions.RequestFailed());
        dispatch(new ErrorNotification(err.userMessages || err.message));
      }

      const reports = await api.loadSavedReports(reportCategories);
      dispatch(new actions.ReceiveReports(ensureEmptyFoldersHaveChildrenProp(reports.rootCodes)));
      navigate.toSelectSavedFavourite();
    },
  removeReport: (id:number, title: string, unshare: boolean = false, 
    reportCategories: number[]): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestReports());
      try {
        await api.removeReport(id, unshare);
        const savedReportState = getState().savedReport as SavedReportState;
        const translate = createDescriptionSelector(savedReportState.texts);
        const messageSuccessRemoveReport = reportCategories?.[0] === ReportCategory.DashboardReport ? 
          savedReportTranslation.success_remove_dashboard : savedReportTranslation.success_remove_report;
        dispatch(new SuccessNotification(
          insertOptionalWordbreaks(replaceTemplateString(translate(messageSuccessRemoveReport), title))
        ));
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
      }
      const reports = await api.loadSavedReports(reportCategories);
      dispatch(new actions.ReceiveReports(ensureEmptyFoldersHaveChildrenProp(reports.rootCodes)));
      const bookmarkedReports = await api.loadBookmarkedSavedReports(reportCategories);
      dispatch(new actions.ReceiveBookmarkedReports(bookmarkedReports.rootCodes));
      navigate.toSelectSavedFavourite();
    }),
  loadFolderData: (id:number): ActionDispatching<any, any> =>
    errorSafe(async (dispatch:any, getState:any) => {
      dispatch(new actions.RequestFolderFavourite());
      const data = await favouriteApi.definition(id);
      dispatch(new actions.ReceiveFolderFavourite(data));
    }),
  clearFolderData: (): ActionDispatching<any, any> =>
    errorSafe((dispatch:any) => {
      dispatch(new actions.ClearFolderData());
    }),
  copySharingInformation: (): ActionDispatching<any, any> =>
    errorSafe((dispatch:any, getState: any) => {
      const savedReportState = getState().savedReport as SavedReportState;

      const update = (id: string, values: Value[]) => {
        const newValue = values && values.map(item => ({value: item.v, description: item.d}));
        dispatch(new ValueChange(STATE.savedReport, id, newValue));
      };

      const validState = savedReportState.folderDefinition && savedReportState.folderDefinition.validState;
      if(validState) {
        update(form.user_group, validState.sharedWithUserGroups);
        update(form.users, validState.sharedWithUsers);
      }
    }),
  loadSavedReports: (reportCategories?: number[]): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestReports());
      const reports = await api.loadSavedReports(reportCategories);
      dispatch(new actions.ReceiveReports(ensureEmptyFoldersHaveChildrenProp(reports.rootCodes)));
    }),
  saveFolder: (data: NewFolderData, reportCategories: number[]): ActionDispatching<any, any> =>
    async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestNewFolder());

      const savedReportState = getState().savedReport as SavedReportState;
      const translate = createDescriptionSelector(savedReportState.texts);

      try {
        const result = await api.saveFolder(data);
        if (result) {
          dispatch(new ValueChange(STATE.savedReport, form.folder, {
            value: result,
            description: data.name
          }));
          dispatch(new SuccessNotification(translate(savedReportTranslation.succes_message_folder)));
          const reports = await api.loadSavedReports(reportCategories);
          dispatch(new actions.ReceiveReports(ensureEmptyFoldersHaveChildrenProp(reports.rootCodes)));
          navigate.toSelectSavedFavourite();
        }
      } catch (err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
      }

      dispatch(new actions.ClearNewFolder());
    },
  moveReport: (reportId:number, parentId:number, reportCategories: number[]): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.RequestReports());
      try{
        await api.moveReport(reportId, {
          parentFolder: parentId
        });
      } catch(err) {
        dispatch(new ErrorNotification(err.userMessages || err.message));
      }
      const reports = await api.loadSavedReports(reportCategories);
      dispatch(new actions.ReceiveReports(ensureEmptyFoldersHaveChildrenProp(reports.rootCodes)));
      navigate.toSelectSavedFavourite();
  }),

  copyLinkNotification: (isSuccess: boolean): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      const savedReportState = getState().savedReport as SavedReportState;
      const translate = createDescriptionSelector(savedReportState.texts);

      isSuccess ? dispatch(new SuccessNotification(translate(savedReportTranslation.link_copy_success)))
        : dispatch(new ErrorNotification(translate(savedReportTranslation.link_copy_fail)));
    }),

  clearAll: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => {
      dispatch(new actions.ClearAll());
    }),

  getSearchRequiredUsers: (): ActionDispatching<any, any> =>
    errorSafe(async (dispatch: any, getState: any) => { 
      dispatch(new actions.RequestSearchRequiredUsers());
      const searchRequiredUsers = await api.searchRequiredUsers();
      dispatch(new actions.ReceiveSearchRequiredUsers(searchRequiredUsers));
    }),

    toUpdateCompanyReport: (favouriteId: any): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {      
        const definition = await favouriteApi.definition(favouriteId);
        const companyReportId = definition?.reports?.filter(r=>r.report?.v === ReportId.CompanyReport)[0]?.specialReport?.v;
        !!companyReportId && routerActions.toUpdateSavedCompanyReport(favouriteId, companyReportId);
    }),

    loadBookmarkedSavedReports: (reportCategories?: number[]): ActionDispatching<any, any> =>
        errorSafe(async (dispatch: any, getState: any) => {
          dispatch(new actions.RequestBookmarkedReports());
          const reports = await api.loadBookmarkedSavedReports(reportCategories);
          dispatch(new actions.ReceiveBookmarkedReports(ensureEmptyFoldersHaveChildrenProp(reports.rootCodes)));
      }),

    removeBookmark: (favouriteId: number,  selectedItem: number, reportCategories: number[]): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(new actions.RequestReports());
        dispatch(new actions.RequestBookmarkedReports());
        try {
          await api.removeBookmark(favouriteId);
        } catch (err) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        const reports = await api.loadSavedReports(reportCategories);
        dispatch(new actions.ReceiveReports(ensureEmptyFoldersHaveChildrenProp(reports.rootCodes)));
        const bookmarkedReports = await api.loadBookmarkedSavedReports(reportCategories);
        dispatch(new actions.ReceiveBookmarkedReports(bookmarkedReports.rootCodes));
        navigate.toSelectSavedFavourite();
    }),

    bookmarkReport: (favouriteId: number, selectedItem: number, reportCategories?: number[]): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(new actions.RequestReports());
        dispatch(new actions.RequestBookmarkedReports());
        try{
          await api.bookmarkReport(favouriteId, {
            bookmark: true
          });
        } catch(err) {
          dispatch(new ErrorNotification(err.userMessages || err.message));
        }
        const reports = await api.loadSavedReports(reportCategories);
        dispatch(new actions.ReceiveReports(ensureEmptyFoldersHaveChildrenProp(reports.rootCodes)));
        const bookmarkedReports = await api.loadBookmarkedSavedReports(reportCategories);
        dispatch(new actions.ReceiveBookmarkedReports(bookmarkedReports.rootCodes));
        navigate.toSelectSavedFavourite();
      }),

    changeViewMode: (mode: ViewMode): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        clientCache.setItem(viewModeCacheKeySavedReports, mode);
        dispatch(new ChangeViewMode(mode));
      }),

    changeViewModeCompanyReport: (mode: ViewMode): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        clientCache.setItem(viewModeCacheKeySavedCompanyReports, mode);
        dispatch(new ChangeViewModeCompanyReport(mode));
      }),

    changeViewModeDashboard: (mode: ViewMode): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        clientCache.setItem(viewModeCacheKeySavedDashboards, mode);
        dispatch(new ChangeViewModeDashboard(mode));
      }),

    toUpdateDashboard: (favouriteId: any): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {      
        const definition = await favouriteApi.definition(favouriteId);
        const dashboardType = definition?.reports?.filter(r=>r.report?.v === ReportId.Dashboard)[0]?.dashboard?.v;
        !!dashboardType && routerActions.toUpdateSavedDashboard(favouriteId, dashboardType);
    })
};
